import { MarkdownRenderer } from "@/components/ui/markdown-renderer";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { and, asc, count, desc, eq, isNull, ne } from "drizzle-orm";
import {
  db,
  ingredientColumnSelection,
  ingredients,
  recipeIngredients,
  recipes,
} from "@/db/index.server";
import { auth } from "@/lib/auth";
import { ContainerSmall } from "@/components/primitives/container-small";
import { PageContainer } from "@/components/primitives/layout";
import {
  LayoutContent2Cols,
  LayoutPageH2,
  LayoutContentAside,
  LayoutContentMain,
  LayoutPageDescription,
  LayoutPageH1,
} from "@/components/primitives/layout";
import { BreadcrumbHeader } from "@/components/primitives/breadcrumb-header";
import { NumberCard } from "@/components/primitives/number-card";
import { CategoryBadge } from "@/components/primitives/category-badge";
import { SourceBadge } from "@/components/primitives/source-badge";
import { Card } from "@/components/ui/card";
import { formatGrams, formatKcal, formatPercentage } from "@/lib/formatters";
import { toNumber } from "@/lib/utils";
import { Nutrition } from "@/components/nutrition";
import {
  calculateIngredientMsnfPct,
  calculateRecipeValues,
} from "@/lib/calculations";
import { BreakdownTable } from "@/components/primitives/breakdown-table";
import { ArrowRightIcon, Users2Icon } from "lucide-react";
import { IngredientDetailActions } from "@/components/ingredient-db/ingredient-detail-actions";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { UnderlineLink } from "@/components/primitives/underline-link";
import { SingleBreadcrumb } from "@/components/primitives/single-breadcrumb";
import { HeaderActions } from "@/components/primitives/header-actions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { buildIngredientDetailsBackHref } from "@/lib/ingredients/ingredientNavigation";

const getIngredientData = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => data)
  .handler(async ({ data }) => {
    const request = getRequest();
    const session = await auth.api.getSession({ headers: request.headers });

    const ingredient = await db._query.ingredients.findFirst({
      where: eq(ingredients.slug, data.slug),
      columns: ingredientColumnSelection,
    });

    if (!ingredient) return null;

    const similarIngredients = await db
      .select({
        id: ingredients.id,
        name: ingredients.name,
        slug: ingredients.slug,
        source: ingredients.source,
        externalId: ingredients.externalId,
        recipeCount: count(recipeIngredients.id),
      })
      .from(ingredients)
      .leftJoin(
        recipeIngredients,
        eq(recipeIngredients.ingredientId, ingredients.id),
      )
      .where(
        and(
          isNull(ingredients.createdBy),
          eq(ingredients.category, ingredient.category),
          ne(ingredients.id, ingredient.id),
        ),
      )
      .groupBy(
        ingredients.id,
        ingredients.name,
        ingredients.slug,
        ingredients.source,
        ingredients.externalId,
      )
      .orderBy(desc(count(recipeIngredients.id)), asc(ingredients.name))
      .limit(5);

    const usedInRecipes = session
      ? await db
          .select({
            id: recipes.id,
            name: recipes.name,
            slug: recipes.slug,
          })
          .from(recipeIngredients)
          .innerJoin(recipes, eq(recipeIngredients.recipeId, recipes.id))
          .where(
            and(
              eq(recipeIngredients.ingredientId, ingredient.id),
              eq(recipes.createdBy, session.user.id),
            ),
          )
          .orderBy(desc(recipes.createdAt))
      : [];

    const isCreatedByOtherUser =
      ingredient.createdBy && ingredient.createdBy !== session?.user?.id;

    return {
      ingredient,
      similarIngredients,
      usedInRecipes,
      isCreatedByOtherUser: !!isCreatedByOtherUser,
    };
  });

export const Route = createFileRoute("/ingredients/$slug")({
  loader: async ({ params }) => {
    const data = await getIngredientData({ data: { slug: params.slug } });
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    return {
      meta: [
        {
          title: `Using ${loaderData.ingredient.name} in ice cream - Scoopulator`,
        },
        {
          name: "description",
          content: `See how ${loaderData.ingredient.name} affects ice cream. Includes FPDF (PAC), sweetness (POD), nutrition, and composition (fat, carbs, sugar, protein, water).`,
        },
      ],
    };
  },
  component: IngredientPage,
});

function IngredientPage() {
  const {
    ingredient,
    similarIngredients,
    usedInRecipes,
    isCreatedByOtherUser,
  } = Route.useLoaderData();
  const [navigationContext] = useQueryStates({
    from: parseAsString,
    q: parseAsString,
    category: parseAsString,
    source: parseAsString,
    page: parseAsInteger,
  });
  const backHref = buildIngredientDetailsBackHref(navigationContext);

  const per100Calculations = calculateRecipeValues(
    [{ ingredient, amountGrams: 100, modifiers: [] }],
    { evaporationPercentage: 0, desiredFrozenWaterPercentage: 0.75 },
  );

  const sugarsPercentage =
    per100Calculations.totalSugarGrams / per100Calculations.totalWeight;
  const lactosePercentage =
    per100Calculations.totalLactoseGrams / per100Calculations.totalWeight;
  const addedSugarsPercentage =
    per100Calculations.totalSugarNonDairyGrams / per100Calculations.totalWeight;
  const notes = ingredient.notes?.trim();

  return (
    <PageContainer>
      <ContainerSmall>
        <BreadcrumbHeader>
          <SingleBreadcrumb href={backHref} label="Back to Ingredients" />
          <HeaderActions>
            <IngredientDetailActions ingredient={ingredient} />
          </HeaderActions>
        </BreadcrumbHeader>
        <LayoutPageH1>{ingredient.name}</LayoutPageH1>
        <LayoutPageDescription>
          See how {ingredient.name} affects ice cream through its composition,
          freezing point depression (FPDF/PAC), and sweetness (POD).
        </LayoutPageDescription>
        {isCreatedByOtherUser ? (
          <Alert className="mt-4" variant="info">
            <Users2Icon />
            <AlertTitle>Created by another user</AlertTitle>
            <AlertDescription>
              You can duplicate this ingredient to make edits and use in your
              own recipes.
            </AlertDescription>
          </Alert>
        ) : null}
        <LayoutContent2Cols>
          <LayoutContentMain>
            <LayoutPageH2>Composition</LayoutPageH2>
            <p className="text-muted-foreground mb-4 text-sm">
              <strong className="font-medium">{ingredient.name}</strong> is made
              up of {formatPercentage(per100Calculations.waterPercentage, 0, 1)}{" "}
              water, {formatPercentage(per100Calculations.fatPercentage, 0, 1)}{" "}
              fat, {formatPercentage(sugarsPercentage, 0, 1)} sugars, and{" "}
              {formatPercentage(
                per100Calculations.totalAlcoholPercentage,
                0,
                1,
              )}{" "}
              alcohol.
            </p>
            <div className="mb-4 md:max-w-md">
              <BreakdownTable
                items={[
                  {
                    component: "Water",
                    percentage: per100Calculations.waterPercentage,
                  },
                  {
                    component: "Fat",
                    percentage: per100Calculations.fatPercentage,
                  },
                  { component: "Sugars", percentage: sugarsPercentage },
                  ...(ingredient.category === "dairy"
                    ? [
                        {
                          component: "Lactose",
                          percentage: lactosePercentage,
                          indent: true,
                        },
                        {
                          component: "Added sugars",
                          percentage: addedSugarsPercentage,
                          indent: true,
                        },
                      ]
                    : []),
                  {
                    component: "Alcohol",
                    percentage: per100Calculations.totalAlcoholPercentage,
                  },
                ]}
              />
            </div>
            <div className="mb-4 flex flex-wrap gap-4">
              <NumberCard
                className="min-w-48"
                title="Total solids"
                value={formatPercentage(
                  per100Calculations.totalSolidsPercentage,
                  0,
                  1,
                )}
              />
              {ingredient.category === "chocolate" ? (
                <NumberCard
                  className="min-w-48"
                  title="Cocoa solids"
                  value={formatPercentage(
                    ingredient.cocoaSolidsNonfatPct,
                    0,
                    1,
                  )}
                />
              ) : null}
              {ingredient.category === "chocolate" ? (
                <NumberCard
                  className="min-w-48"
                  title="Cocoa butter"
                  value={formatPercentage(ingredient.cocoaFatPct, 0, 1)}
                />
              ) : null}
              {ingredient.category === "dairy" ? (
                <NumberCard
                  className="min-w-48"
                  title={
                    <span>
                      MSNF
                      <HelpTooltip>
                        Milk solids-not-fat (MSNF) refers to the components of
                        milk other than water and fat, including proteins,
                        lactose, and minerals.
                      </HelpTooltip>
                    </span>
                  }
                  value={formatPercentage(
                    calculateIngredientMsnfPct(ingredient),
                    0,
                    1,
                  )}
                />
              ) : null}
              {toNumber(ingredient.emulsifierPct) > 0 &&
              toNumber(ingredient.emulsifierPct) < 1 ? (
                <NumberCard
                  className="min-w-48"
                  title="Emulsifiers"
                  value={formatPercentage(
                    toNumber(ingredient.emulsifierPct),
                    0,
                    1,
                  )}
                />
              ) : null}
              {toNumber(ingredient.stabilizerPct) > 0 ? (
                <NumberCard
                  className="min-w-48"
                  title="Stabilizers"
                  value={formatPercentage(toNumber(ingredient.stabilizerPct))}
                />
              ) : null}
            </div>

            <div className="mt-8">
              <LayoutPageH2>Freezing properties</LayoutPageH2>
              <p className="text-muted-foreground mb-1 text-sm">
                Certain ingredients lower the freezing point of water, which is
                what makes ice cream soft and scoopable.
              </p>
              <p className="mb-4">
                <UnderlineLink
                  to="/learn/fpdf"
                  className="flex items-center gap-1 text-sm"
                >
                  Learn more about freezing point depression{" "}
                  <ArrowRightIcon size={14} />
                </UnderlineLink>
              </p>
              <div className="flex flex-wrap gap-4">
                <NumberCard
                  title="Freezing point depression factor"
                  description="(Also known as PAC)"
                  value={formatPercentage(toNumber(ingredient.pac), 0, 1)}
                  valueDescription={`Similar to ${formatGrams(toNumber(ingredient.pac) * 100)} of sucrose dissolved in 100g of water`}
                />
              </div>
            </div>

            <div className="mt-8">
              <LayoutPageH2>Sweetness</LayoutPageH2>
              <p className="text-muted-foreground mb-4 text-sm">
                <strong className="font-medium">{ingredient.name}</strong> is{" "}
                {toNumber(ingredient.pod) >= 1
                  ? `${toNumber(ingredient.pod).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 0,
                    })} times`
                  : formatPercentage(toNumber(ingredient.pod))}{" "}
                as sweet as table sugar.
              </p>
              <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                <NumberCard
                  title="Relative sweetness"
                  description="(Also known as POD)"
                  value={formatPercentage(toNumber(ingredient.pod))}
                />
              </div>
            </div>

            <div className="mt-8">
              <LayoutPageH2>Nutrition</LayoutPageH2>
              <p className="text-muted-foreground mb-4 text-sm">
                <strong className="font-medium">{ingredient.name}</strong> has{" "}
                {formatKcal(per100Calculations.totalEnergyKcal)} per 100g.
              </p>
              <Nutrition
                calculations={per100Calculations}
                includeFullRecipeAmounts={false}
                hideHeader
              />
            </div>
          </LayoutContentMain>
          <LayoutContentAside>
            <Card>
              <Table>
                <TableBody>
                  {ingredient.aliases && ingredient.aliases.length > 0 ? (
                    <TableRow>
                      <TableHead>Also known as</TableHead>
                      <TableCell>{ingredient.aliases.join(", ")}</TableCell>
                    </TableRow>
                  ) : null}
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableCell>
                      <CategoryBadge category={ingredient.category} size="sm" />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableCell>
                      {ingredient.source ? (
                        <SourceBadge
                          source={ingredient.source}
                          externalId={ingredient.externalId}
                          size="sm"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableCell>
                      {new Date(ingredient.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableHead>Updated</TableHead>
                    <TableCell>
                      {new Date(ingredient.updatedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
            {notes ? (
              <div className="mt-6">
                <h3 className="mb-2 font-medium">Notes</h3>
                <MarkdownRenderer content={notes} className="text-sm" />
              </div>
            ) : null}
            <div className="mt-6">
              <h3 className="mb-2 font-medium">
                Used in {usedInRecipes.length} of your recipes
              </h3>
              {usedInRecipes.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  This ingredient hasn&apos;t been used in any of your recipes
                  yet.
                </p>
              ) : (
                <ul className="space-y-1">
                  {usedInRecipes.map((r: (typeof usedInRecipes)[number]) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-2"
                    >
                      <UnderlineLink
                        className="truncate"
                        to={`/recipes/${r.slug}`}
                      >
                        {r.name}
                      </UnderlineLink>
                    </li>
                  ))}
                </ul>
              )}
              <div className="bg-border my-4 h-px w-full" />
              <h3 className="mb-2 font-medium">Similar ingredients</h3>
              {similarIngredients.length === 0 ? (
                <p className="text-muted-foreground text-sm">None found</p>
              ) : (
                <ul className="space-y-1">
                  {similarIngredients.map(
                    (si: (typeof similarIngredients)[number]) => (
                      <li
                        key={si.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <UnderlineLink
                          className="truncate"
                          to={`/ingredients/${si.slug}`}
                        >
                          {si.name}
                        </UnderlineLink>
                      </li>
                    ),
                  )}
                </ul>
              )}
            </div>
          </LayoutContentAside>
        </LayoutContent2Cols>
      </ContainerSmall>
    </PageContainer>
  );
}
